import { motion } from "framer-motion";
import { X, Check, Zap, Puzzle, MessagesSquare, Target } from "lucide-react";
import { ChapterHeading, MaskedLines, Reveal } from "@/components/Reveal";

const NOT_LIST = [
  { title: "Não somos uma fábrica de software.", text: "Não desenvolvemos qualquer sistema sob demanda. Fazemos uma coisa — e fazemos muito bem." },
  { title: "Não somos um chatbot genérico.", text: "Nossa solução foi criada especificamente para atendimento e agendamento. Nada de menus numéricos travados." },
  { title: "Não atendemos qualquer segmento.", text: "Nosso foco são negócios locais de serviço, onde atendimento e agenda fazem parte do dia a dia." },
  { title: "Não substituímos sua equipe.", text: "Assumimos as tarefas repetitivas para que pessoas cuidem do que exige atenção humana." },
];

const DIFFS = [
  { icon: Zap, title: "Implementação rápida", text: "Do diagnóstico à ativação em poucos dias — não meses." },
  { icon: Puzzle, title: "Feito para o seu negócio", text: "Configurado com seus serviços, regras, horários e tom de voz." },
  { icon: MessagesSquare, title: "Conversas naturais", text: "IA que conversa como gente, não como formulário." },
  { icon: Target, title: "Foco em resultado", text: "Métricas claras de atendimento, agenda e receita recuperada." },
];

export default function NotUs() {
  return (
    <section id="posicionamento" data-testid="notus-section" className="grain relative overflow-hidden bg-navy py-28 text-white sm:py-36">
      <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10">
        <div className="grid gap-16 lg:grid-cols-2 lg:gap-10">
          <div>
            <Reveal>
              <div className="flex items-center gap-4">
                <span className="font-mono2 text-xs tracking-[0.35em] text-turquoise">06</span>
                <span className="h-px w-12 bg-turquoise" />
                <span className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-sage">Posicionamento</span>
              </div>
            </Reveal>
            <MaskedLines
              lines={["A CliniAtiva não é", "para todo mundo.", "E isso é proposital."]}
              delay={0.1}
              className="mt-6 font-display text-3xl font-bold leading-[1.05] tracking-tight text-offwhite sm:text-4xl lg:text-5xl"
            />
            <div className="mt-12 space-y-0">
              {NOT_LIST.map((n, i) => (
                <motion.div
                  key={n.title}
                  data-testid={`notus-item-${i + 1}`}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  className="group flex gap-5 border-b border-white/10 py-7 transition-colors duration-300 hover:border-turquoise/40"
                >
                  <span className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-red-400/40 text-red-300">
                    <X className="h-4 w-4" />
                  </span>
                  <div>
                    <h3 className="font-display text-lg font-bold text-white">{n.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-sage/75">{n.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="lg:pl-10">
            <Reveal delay={0.15}>
              <p className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-turquoise">O que somos</p>
              <h3 className="mt-4 font-display text-2xl font-bold text-offwhite sm:text-3xl">
                Especialistas em atendimento e agendamento local.
              </h3>
            </Reveal>
            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              {DIFFS.map((d, i) => (
                <motion.div
                  key={d.title}
                  data-testid={`diff-card-${i + 1}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: 0.1 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ y: -4 }}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm transition-colors duration-300 hover:border-turquoise/40"
                >
                  <div className="flex items-center justify-between">
                    <d.icon className="h-6 w-6 text-turquoise" />
                    <Check className="h-4 w-4 text-sage/50" />
                  </div>
                  <h4 className="mt-5 font-display text-base font-bold text-white">{d.title}</h4>
                  <p className="mt-2 text-sm leading-relaxed text-sage/75">{d.text}</p>
                </motion.div>
              ))}
            </div>
            <Reveal delay={0.3} className="mt-10">
              <p className="border-l-2 border-turquoise pl-5 font-display text-lg font-medium italic leading-relaxed text-sage">
                “Preferimos ser indispensáveis para alguns segmentos a ser irrelevantes para todos.”
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
